import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTPageURLV2ToSDKPageURLV2 } from '@wix/sdk-runtime/transformations/page-url-v2';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddOnGroup,
  BulkCreateServicesOptions,
  BulkCreateServicesResponse,
  BulkDeleteServicesByFilterOptions,
  BulkDeleteServicesByFilterResponse,
  BulkDeleteServicesOptions,
  BulkDeleteServicesResponse,
  BulkUpdateServicesByFilterOptions,
  BulkUpdateServicesByFilterResponse,
  BulkUpdateServicesOptions,
  BulkUpdateServicesResponse,
  CloneServiceResponse,
  CountServicesOptions,
  CountServicesResponse,
  CreateAddOnGroupOptions,
  CreateAddOnGroupResponse,
  CreateServiceValidationErrors,
  CursorQuery,
  DeleteAddOnGroupApplicationErrors,
  DeleteAddOnGroupOptions,
  DeleteServiceOptions,
  DisablePricingPlansForServiceApplicationErrors,
  DisablePricingPlansForServiceOptions,
  DisablePricingPlansForServiceResponse,
  EnablePricingPlansForServiceApplicationErrors,
  EnablePricingPlansForServiceResponse,
  ListAddOnGroupsByServiceIdOptions,
  ListAddOnGroupsByServiceIdResponse,
  QueryBookingFormsApplicationErrors,
  QueryBookingFormsOptions,
  QueryBookingFormsResponse,
  QueryCategoriesOptions,
  QueryCategoriesResponse,
  QueryLocationsOptions,
  QueryLocationsResponse,
  QueryPoliciesResponse,
  QueryServicesOptions,
  QueryServicesResponse,
  SearchServicesResponse,
  Service,
  ServiceCreatedEnvelope,
  ServiceDeletedEnvelope,
  ServiceQuery,
  ServiceSearch,
  ServiceUpdatedEnvelope,
  ServicesQueryBuilder,
  SetAddOnsForGroupApplicationErrors,
  SetAddOnsForGroupOptions,
  SetAddOnsForGroupResponse,
  SetCustomSlugApplicationErrors,
  SetCustomSlugOptions,
  SetCustomSlugResponse,
  SetCustomSlugValidationErrors,
  SetServiceLocationsOptions,
  SetServiceLocationsResponse,
  UpdateAddOnGroupApplicationErrors,
  UpdateAddOnGroupOptions,
  UpdateAddOnGroupResponse,
  UpdateService,
  UpdateServiceValidationErrors,
  ValidateSlugOptions,
  ValidateSlugResponse,
  bulkCreateServices as universalBulkCreateServices,
  bulkDeleteServices as universalBulkDeleteServices,
  bulkDeleteServicesByFilter as universalBulkDeleteServicesByFilter,
  bulkUpdateServices as universalBulkUpdateServices,
  bulkUpdateServicesByFilter as universalBulkUpdateServicesByFilter,
  cloneService as universalCloneService,
  countServices as universalCountServices,
  createAddOnGroup as universalCreateAddOnGroup,
  createService as universalCreateService,
  deleteAddOnGroup as universalDeleteAddOnGroup,
  deleteService as universalDeleteService,
  disablePricingPlansForService as universalDisablePricingPlansForService,
  enablePricingPlansForService as universalEnablePricingPlansForService,
  getService as universalGetService,
  listAddOnGroupsByServiceId as universalListAddOnGroupsByServiceId,
  queryBookingForms as universalQueryBookingForms,
  queryCategories as universalQueryCategories,
  queryLocations as universalQueryLocations,
  queryPolicies as universalQueryPolicies,
  queryServices as universalQueryServices,
  searchServices as universalSearchServices,
  setAddOnsForGroup as universalSetAddOnsForGroup,
  setCustomSlug as universalSetCustomSlug,
  setServiceLocations as universalSetServiceLocations,
  typedQueryServices as universalTypedQueryServices,
  updateAddOnGroup as universalUpdateAddOnGroup,
  updateService as universalUpdateService,
  validateSlug as universalValidateSlug,
} from './bookings-services-v2-service-services.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/bookings' };

export function createService(httpClient: HttpClient): CreateServiceSignature {
  return (service: Service) =>
    universalCreateService(
      service,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateServiceSignature {
  /**
   * Creates a service.
   *
   *
   * ### Required fields
   *
   * When creating a service you must specify the following fields:
   * - [`type`](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-types):
   * Whether it's an appointment-based service, class, or course.
   * - `name`: Service name that's displayed to customers.
   * - `onlineBooking`:
   * Settings determining whether customers can book online, whether the business
   * must manually confirm bookings, and whether customers can request to book an
   * appointment time slot that already has a booking request awaiting business
   * confirmation.
   * - [`payment`](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments):
   * How customers can pay when signing up for the service.
   * - `defaultCapacity`: The maximum number of customers that can book the service. Required for all service types.
   * - For appointment-based services, it must be `1`.
   * - For classes, set to the maximum number of participants per session.
   * - For courses, set to the maximum number of participants for the course.
   *
   * ### Category
   *
   * Set `category.id` to assign the service to a [category](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/categories/introduction).
   * Without an assigned category, the service isn't visible on the live site.
   *
   * ### Online booking
   *
   * Set `onlineBooking.enabled` to `true` so customers can book the service online.
   * When enabled, the service is included in the [single-service booking flow](https://dev.wix.com/docs/api-reference/business-solutions/bookings/flow-single-service-booking).
   *
   * ### Approval
   *
   * Set `onlineBooking.requireManualApproval` to control whether bookings need business approval before confirmation.
   * Services with manual approval enabled can't accept pricing plan payments.
   *
   * ### Payment
   *
   * Set `payment.rateType` to `FIXED`, `VARIED`, `CUSTOM`, or `NO_FEE` and specify the corresponding rate details:
   * - `FIXED`: Set `payment.fixed.price`.
   * - `VARIED`: Set `payment.varied.defaultPrice`.
   * - `CUSTOM`: Set `payment.custom.description`.
   * - `NO_FEE`: No additional rate details required.
   *
   * Configure `payment.options` to control how customers can pay:
   * - `online`: Customers can pay online. Requires `rateType` of `FIXED` or `VARIED`.
   * - `inPerson`: Customers can pay in person.
   * - `deposit`: A deposit is required to book. Requires `rateType` of `FIXED` or `VARIED` and a `deposit` amount in the rate details.
   * - `pricingPlan`: Customers can pay using a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/plans/introduction).
   * Can't be used in combination with manual approval.
   *
   * Learn more about [service payments](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).
   *
   * ### Session durations
   *
   * Depending on which type of service you're creating, you may also need to specify
   * supported session durations.
   *
   * __Classes and courses__
   *
   * Don't specify `schedule.availabilityConstraints.sessionDurations`.
   *
   * __Appointment-based services without varied pricing based on session length__
   *
   * Specify the single supported session duration in the
   * `schedule.availabilityConstraints.sessionDurations` array.
   *
   * __Appointment-based services with varied pricing based on session length__
   *
   * - Specify all supported session durations in `schedule.availabilityConstraints.sessionDurations`.
   * - Note that Wix Bookings doesn't display these values to customers and
   * ignores them in pricing and availability calculations. Instead session
   * durations are retrieved from the relevant [service variants](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/service-options-and-variants/introduction).
   * - It is mandatory to specify `schedule.availabilityConstraints.sessionDurations`,
   * even though these values are ignored.
   *
   * ### Buffer time
   *
   * For appointment-based services, you can set `schedule.availabilityConstraints.timeBetweenSessions` to add buffer time between booked sessions.
   *
   * ### Locations
   *
   * Set the service's location types and options. You can set 1, some, or all types simultaneously. See [About service locations](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-locations) for details.
   *
   * ### Media
   *
   * Specify `media` so customers see images when selecting the service.
   * Set `media.mainMedia` as the primary image and optionally `media.coverMedia` or `media.items`.
   * Each item is an image with `id`, `height`, and `width`, and optionally `url` and `altText`.
   * Refer to the [Media Manager API](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction) for details about uploading images and retrieving their IDs.
   *
   * ### Add-on groups
   *
   * Don't specify existing add-on groups when calling Create Service.
   * Instead, use dedicated add-on group and add-on creation methods to ensure proper validation and maintain referential integrity between services and their optional extras.
   * You can follow this [sample flow](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/sample-flows#create-bookable-add-ons-for-a-new-group) to create bookable add-ons.
   * @param - Service to create.
   * @returns Created service.
   */
  (service: Service): Promise<
    NonNullablePaths<
      Service,
      | `type`
      | `media.items`
      | `category._id`
      | `form._id`
      | `payment.fixed.price.value`
      | `payment.fixed.price.currency`
      | `payment.rateType`
      | `payment.pricingPlanIds`
      | `payment.addOnOption`
      | `locations`
      | `locations.${number}.business._id`
      | `locations.${number}.business.name`
      | `locations.${number}.custom._id`
      | `locations.${number}._id`
      | `locations.${number}.type`
      | `bookingPolicy._id`
      | `bookingPolicy.customPolicyDescription.enabled`
      | `bookingPolicy.customPolicyDescription.description`
      | `bookingPolicy.limitEarlyBookingPolicy.enabled`
      | `bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
      | `bookingPolicy.limitLateBookingPolicy.enabled`
      | `bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
      | `bookingPolicy.bookAfterStartPolicy.enabled`
      | `bookingPolicy.cancellationPolicy.enabled`
      | `bookingPolicy.cancellationPolicy.limitLatestCancellation`
      | `bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
      | `bookingPolicy.reschedulePolicy.enabled`
      | `bookingPolicy.reschedulePolicy.limitLatestReschedule`
      | `bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
      | `bookingPolicy.waitlistPolicy.enabled`
      | `bookingPolicy.waitlistPolicy.capacity`
      | `bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
      | `bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
      | `bookingPolicy.resourcesPolicy.enabled`
      | `bookingPolicy.resourcesPolicy.autoAssignAllowed`
      | `bookingPolicy.cancellationFeePolicy.enabled`
      | `bookingPolicy.cancellationFeePolicy.cancellationWindows`
      | `bookingPolicy.cancellationFeePolicy.cancellationWindows.${number}.percentage`
      | `bookingPolicy.saveCreditCardPolicy.enabled`
      | `schedule.availabilityConstraints.durations`
      | `schedule.availabilityConstraints.durations.${number}.minutes`
      | `schedule.availabilityConstraints.sessionDurations`
      | `schedule.availabilityConstraints.timeBetweenSessions`
      | `staffMemberIds`
      | `serviceResources`
      | `supportedSlugs`
      | `supportedSlugs.${number}.name`
      | `seoData.tags`
      | `seoData.tags.${number}.type`
      | `seoData.tags.${number}.children`
      | `seoData.tags.${number}.custom`
      | `seoData.tags.${number}.disabled`
      | `seoData.settings.preventAutoRedirect`
      | `seoData.settings.keywords`
      | `seoData.settings.keywords.${number}.term`
      | `seoData.settings.keywords.${number}.isMain`
      | `addOnGroups`
      | `addOnDetails`
      | `taxableAddress.taxableAddressType`,
      6
    > & {
      __validationErrorsType?: CreateServiceValidationErrors;
    }
  >;
}

export function bulkCreateServices(
  httpClient: HttpClient
): BulkCreateServicesSignature {
  return (services: Service[], options?: BulkCreateServicesOptions) =>
    universalBulkCreateServices(
      services,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateServicesSignature {
  /**
   * Creates multiple services.
   *
   *
   * See [Create Service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/create-service) for more details.
   * @param - Services to create.
   */
  (services: Service[], options?: BulkCreateServicesOptions): Promise<
    NonNullablePaths<
      BulkCreateServicesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.type`
      | `results.${number}.item.category._id`
      | `results.${number}.item.form._id`
      | `results.${number}.item.payment.rateType`
      | `results.${number}.item.payment.addOnOption`
      | `results.${number}.item.bookingPolicy._id`
      | `results.${number}.item.taxableAddress.taxableAddressType`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function getService(httpClient: HttpClient): GetServiceSignature {
  return (serviceId: string) =>
    universalGetService(
      serviceId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetServiceSignature {
  /**
   * Retrieves a service.
   * @param - ID of the service to retrieve.
   * @returns Retrieved service.
   */
  (serviceId: string): Promise<
    NonNullablePaths<
      Service,
      | `type`
      | `media.items`
      | `category._id`
      | `form._id`
      | `payment.fixed.price.value`
      | `payment.fixed.price.currency`
      | `payment.rateType`
      | `payment.pricingPlanIds`
      | `payment.addOnOption`
      | `locations`
      | `locations.${number}.business._id`
      | `locations.${number}.business.name`
      | `locations.${number}.custom._id`
      | `locations.${number}._id`
      | `locations.${number}.type`
      | `bookingPolicy._id`
      | `bookingPolicy.customPolicyDescription.enabled`
      | `bookingPolicy.customPolicyDescription.description`
      | `bookingPolicy.limitEarlyBookingPolicy.enabled`
      | `bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
      | `bookingPolicy.limitLateBookingPolicy.enabled`
      | `bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
      | `bookingPolicy.bookAfterStartPolicy.enabled`
      | `bookingPolicy.cancellationPolicy.enabled`
      | `bookingPolicy.cancellationPolicy.limitLatestCancellation`
      | `bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
      | `bookingPolicy.reschedulePolicy.enabled`
      | `bookingPolicy.reschedulePolicy.limitLatestReschedule`
      | `bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
      | `bookingPolicy.waitlistPolicy.enabled`
      | `bookingPolicy.waitlistPolicy.capacity`
      | `bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
      | `bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
      | `bookingPolicy.resourcesPolicy.enabled`
      | `bookingPolicy.resourcesPolicy.autoAssignAllowed`
      | `bookingPolicy.cancellationFeePolicy.enabled`
      | `bookingPolicy.cancellationFeePolicy.cancellationWindows`
      | `bookingPolicy.cancellationFeePolicy.cancellationWindows.${number}.percentage`
      | `bookingPolicy.saveCreditCardPolicy.enabled`
      | `schedule.availabilityConstraints.durations`
      | `schedule.availabilityConstraints.durations.${number}.minutes`
      | `schedule.availabilityConstraints.sessionDurations`
      | `schedule.availabilityConstraints.timeBetweenSessions`
      | `staffMemberIds`
      | `serviceResources`
      | `supportedSlugs`
      | `supportedSlugs.${number}.name`
      | `seoData.tags`
      | `seoData.tags.${number}.type`
      | `seoData.tags.${number}.children`
      | `seoData.tags.${number}.custom`
      | `seoData.tags.${number}.disabled`
      | `seoData.settings.preventAutoRedirect`
      | `seoData.settings.keywords`
      | `seoData.settings.keywords.${number}.term`
      | `seoData.settings.keywords.${number}.isMain`
      | `addOnGroups`
      | `addOnDetails`
      | `taxableAddress.taxableAddressType`,
      6
    >
  >;
}

export function updateService(httpClient: HttpClient): UpdateServiceSignature {
  return (
    _id: string,
    service: NonNullablePaths<UpdateService, `revision`, 2>
  ) =>
    universalUpdateService(
      _id,
      service,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateServiceSignature {
  /**
   * Updates a service.
   *
   *
   * Each time the service is updated, `revision` increments by 1. You must
   * include the number of the existing revision when updating the service.
   * This ensures you're working with the latest service information and
   * prevents unintended overwrites.
   *
   * ### Session durations
   *
   * Specify `schedule.availabilityConstraints.sessionDurations`
   * only if you want to update it for appointment-based services without varied
   * pricing based on session length. Don't specify `schedule.availabilityConstraints.sessionDurations`
   * for all other appointment-based services, classes, or courses. See [Create Service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/create-service#session-durations) for more details.
   *
   * ### Service locations
   *
   * Don't call Update Service to adjust service locations, call [Set Service Locations](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/set-service-locations) instead.
   *
   * ### Add-on groups
   *
   * Don't use Update Service to modify service add-on groups. Use these dedicated methods instead:
   * - **Update add-on group properties**: Call Update Add On Group ([SDK](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/update-add-on-group?apiView=SDK) | [REST](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/update-add-on-group)).
   * - **Update add-on display order**: Call Set Add Ons For Group ([SDK](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/set-add-ons-for-group?apiView=SDK) | [REST](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/set-add-ons-for-group)).
   * @param - Service ID.
   * @returns Updated service.
   */
  (
    _id: string,
    service: NonNullablePaths<UpdateService, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      Service,
      | `type`
      | `media.items`
      | `category._id`
      | `form._id`
      | `payment.fixed.price.value`
      | `payment.fixed.price.currency`
      | `payment.rateType`
      | `payment.pricingPlanIds`
      | `payment.addOnOption`
      | `locations`
      | `locations.${number}.business._id`
      | `locations.${number}.business.name`
      | `locations.${number}.custom._id`
      | `locations.${number}._id`
      | `locations.${number}.type`
      | `bookingPolicy._id`
      | `bookingPolicy.customPolicyDescription.enabled`
      | `bookingPolicy.customPolicyDescription.description`
      | `bookingPolicy.limitEarlyBookingPolicy.enabled`
      | `bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
      | `bookingPolicy.limitLateBookingPolicy.enabled`
      | `bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
      | `bookingPolicy.bookAfterStartPolicy.enabled`
      | `bookingPolicy.cancellationPolicy.enabled`
      | `bookingPolicy.cancellationPolicy.limitLatestCancellation`
      | `bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
      | `bookingPolicy.reschedulePolicy.enabled`
      | `bookingPolicy.reschedulePolicy.limitLatestReschedule`
      | `bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
      | `bookingPolicy.waitlistPolicy.enabled`
      | `bookingPolicy.waitlistPolicy.capacity`
      | `bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
      | `bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
      | `bookingPolicy.resourcesPolicy.enabled`
      | `bookingPolicy.resourcesPolicy.autoAssignAllowed`
      | `bookingPolicy.cancellationFeePolicy.enabled`
      | `bookingPolicy.cancellationFeePolicy.cancellationWindows`
      | `bookingPolicy.cancellationFeePolicy.cancellationWindows.${number}.percentage`
      | `bookingPolicy.saveCreditCardPolicy.enabled`
      | `schedule.availabilityConstraints.durations`
      | `schedule.availabilityConstraints.durations.${number}.minutes`
      | `schedule.availabilityConstraints.sessionDurations`
      | `schedule.availabilityConstraints.timeBetweenSessions`
      | `staffMemberIds`
      | `serviceResources`
      | `supportedSlugs`
      | `supportedSlugs.${number}.name`
      | `seoData.tags`
      | `seoData.tags.${number}.type`
      | `seoData.tags.${number}.children`
      | `seoData.tags.${number}.custom`
      | `seoData.tags.${number}.disabled`
      | `seoData.settings.preventAutoRedirect`
      | `seoData.settings.keywords`
      | `seoData.settings.keywords.${number}.term`
      | `seoData.settings.keywords.${number}.isMain`
      | `addOnGroups`
      | `addOnDetails`
      | `taxableAddress.taxableAddressType`,
      6
    > & {
      __validationErrorsType?: UpdateServiceValidationErrors;
    }
  >;
}

export function bulkUpdateServices(
  httpClient: HttpClient
): BulkUpdateServicesSignature {
  return (
    options?: NonNullablePaths<
      BulkUpdateServicesOptions,
      | `services.${number}.service`
      | `services.${number}.service._id`
      | `services.${number}.service.revision`,
      5
    >
  ) =>
    universalBulkUpdateServices(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateServicesSignature {
  /**
   * Updates up to 100 services.
   *
   *
   * See [Update Service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/update-service)
   * for more details.
   *
   * The call succeeds even if one or more individual services can't be updated.
   * Information about failures is returned in `bulkActionMetadata`.
   */
  (
    options?: NonNullablePaths<
      BulkUpdateServicesOptions,
      | `services.${number}.service`
      | `services.${number}.service._id`
      | `services.${number}.service.revision`,
      5
    >
  ): Promise<
    NonNullablePaths<
      BulkUpdateServicesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.type`
      | `results.${number}.item.category._id`
      | `results.${number}.item.form._id`
      | `results.${number}.item.payment.rateType`
      | `results.${number}.item.payment.addOnOption`
      | `results.${number}.item.bookingPolicy._id`
      | `results.${number}.item.taxableAddress.taxableAddressType`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateServicesByFilter(
  httpClient: HttpClient
): BulkUpdateServicesByFilterSignature {
  return (
    filter: Record<string, any>,
    options: NonNullablePaths<BulkUpdateServicesByFilterOptions, `service`, 2>
  ) =>
    universalBulkUpdateServicesByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateServicesByFilterSignature {
  /**
   * Updates multiple services by filter.
   *
   *
   * See [Query Services](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/query-services) for a list of supported filters.
   *
   * See [Update Service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/update-service) for more details about updating a service.
   *
   * The call succeeds even if one or more individual services can't be updated.
   * Information about failures is returned in `bulkActionMetadata`.
   * @param - Filter to identify the services to update.
   */
  (
    filter: Record<string, any>,
    options: NonNullablePaths<BulkUpdateServicesByFilterOptions, `service`, 2>
  ): Promise<NonNullablePaths<BulkUpdateServicesByFilterResponse, `jobId`, 2>>;
}

export function deleteService(httpClient: HttpClient): DeleteServiceSignature {
  return (serviceId: string, options?: DeleteServiceOptions) =>
    universalDeleteService(
      serviceId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteServiceSignature {
  /**
   * Deletes a service.
   *
   *
   * Specify `{"preserveFutureSessionsWithParticipants": true}` to retain all
   * future sessions for the service. By default, all future sessions are canceled.
   * @param - ID of the service to delete.
   * @param - Allows you to configure how to handle the deleted service's future sessions and how to notify the sessions participants.
   */
  (serviceId: string, options?: DeleteServiceOptions): Promise<void>;
}

export function bulkDeleteServices(
  httpClient: HttpClient
): BulkDeleteServicesSignature {
  return (ids: string[], options?: BulkDeleteServicesOptions) =>
    universalBulkDeleteServices(
      ids,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteServicesSignature {
  /**
   * Deletes multiple services.
   *
   *
   * See [Delete Service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/delete-service)
   * for more details about deleting a service.
   *
   * The call succeeds even if one or more individual services can't be deleted.
   * Information about failures is returned in `bulkActionMetadata`.
   * @param - IDs of the services to delete.
   */
  (ids: string[], options?: BulkDeleteServicesOptions): Promise<
    NonNullablePaths<
      BulkDeleteServicesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.type`
      | `results.${number}.item.category._id`
      | `results.${number}.item.form._id`
      | `results.${number}.item.payment.rateType`
      | `results.${number}.item.payment.addOnOption`
      | `results.${number}.item.bookingPolicy._id`
      | `results.${number}.item.taxableAddress.taxableAddressType`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkDeleteServicesByFilter(
  httpClient: HttpClient
): BulkDeleteServicesByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkDeleteServicesByFilterOptions
  ) =>
    universalBulkDeleteServicesByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteServicesByFilterSignature {
  /**
   * Deletes multiple services by filter.
   *
   *
   * See [Delete Service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/delete-service)
   * for more details about deleting a service.
   *
   * The call succeeds even if one or more individual services can't be deleted.
   * Information about failures is returned in `bulkActionMetadata`.
   *
   * Refer to the [supported filters article](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction#services-filtering-and-sorting)
   * for more details.
   *
   * To learn about working with filters in general, see
   * [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   * @param - Filter to identify the services that need to be deleted.
   */
  (
    filter: Record<string, any>,
    options?: BulkDeleteServicesByFilterOptions
  ): Promise<NonNullablePaths<BulkDeleteServicesByFilterResponse, `jobId`, 2>>;
}

export function queryServices(httpClient: HttpClient): QueryServicesSignature {
  return (options?: QueryServicesOptions) =>
    universalQueryServices(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryServicesSignature {
  /**
   * Creates a query to retrieve a list of `service` objects.
   *
   * The `queryServices()` function builds a query to retrieve a list of `service` objects and returns a `ServicesQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the [find()](https://dev.wix.com/docs/sdk/backend-modules/bookings/services/services-query-builder/find) function.
   *
   * You can refine the query by chaining `ServicesQueryBuilder` functions onto the query. `ServicesQueryBuilder` functions enable you to sort, filter, and control the results that `queryServices()` returns.
   *
   * `queryServices()` runs with the following `ServicesQueryBuilder` defaults that you can override:
   *
   * + `limit` is `100`.
   * + Sorted by `createdDate` in ascending order.
   *
   * The functions that are chained to `queryServices()` are applied in the order they are called. For example, if you apply `ascending("category.name")` and then `ascending("name")`, the results are sorted first by `category.name`, and then, if there are multiple results with the same `category.name`, the items are sorted by `name`.
   *
   * The following `ServicesQueryBuilder` functions are supported for the `queryServices()` function. For a full description of the `service` object, see the object returned for the [items](https://dev.wix.com/docs/sdk/backend-modules/bookings/services/services-query-result/items) property in `ServicesQueryResult`.
   */
  (options?: QueryServicesOptions): ServicesQueryBuilder;
}

export function typedQueryServices(
  httpClient: HttpClient
): TypedQueryServicesSignature {
  return (query: ServiceQuery, options?: QueryServicesOptions) =>
    universalTypedQueryServices(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryServicesSignature {
  /** */
  (query: ServiceQuery, options?: QueryServicesOptions): Promise<
    NonNullablePaths<
      QueryServicesResponse,
      | `services`
      | `services.${number}.type`
      | `services.${number}.category._id`
      | `services.${number}.form._id`
      | `services.${number}.payment.rateType`
      | `services.${number}.payment.addOnOption`
      | `services.${number}.bookingPolicy._id`
      | `services.${number}.bookingPolicy.customPolicyDescription.enabled`
      | `services.${number}.bookingPolicy.customPolicyDescription.description`
      | `services.${number}.bookingPolicy.limitEarlyBookingPolicy.enabled`
      | `services.${number}.bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
      | `services.${number}.bookingPolicy.limitLateBookingPolicy.enabled`
      | `services.${number}.bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
      | `services.${number}.bookingPolicy.bookAfterStartPolicy.enabled`
      | `services.${number}.bookingPolicy.cancellationPolicy.enabled`
      | `services.${number}.bookingPolicy.cancellationPolicy.limitLatestCancellation`
      | `services.${number}.bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
      | `services.${number}.bookingPolicy.reschedulePolicy.enabled`
      | `services.${number}.bookingPolicy.reschedulePolicy.limitLatestReschedule`
      | `services.${number}.bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
      | `services.${number}.bookingPolicy.waitlistPolicy.enabled`
      | `services.${number}.bookingPolicy.waitlistPolicy.capacity`
      | `services.${number}.bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
      | `services.${number}.bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
      | `services.${number}.bookingPolicy.resourcesPolicy.enabled`
      | `services.${number}.bookingPolicy.resourcesPolicy.autoAssignAllowed`
      | `services.${number}.bookingPolicy.cancellationFeePolicy.enabled`
      | `services.${number}.bookingPolicy.saveCreditCardPolicy.enabled`
      | `services.${number}.schedule.availabilityConstraints.timeBetweenSessions`
      | `services.${number}.seoData.settings.preventAutoRedirect`
      | `services.${number}.taxableAddress.taxableAddressType`,
      6
    >
  >;
}

export function searchServices(
  httpClient: HttpClient
): SearchServicesSignature {
  return (search: ServiceSearch) =>
    universalSearchServices(
      search,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchServicesSignature {
  /**
   * Retrieves a list of up to 100 services, given the provided filtering, paging,
   * and sorting.
   *
   *
   * ### Defaults
   *
   * Search Services has the following default settings, which you can override:
   * + Sorted by `createdDate` in ascending order.
   * + `paging.limit` set to `100`.
   * + `paging.offset` set to `0`.
   *
   * ### Filters
   *
   * When using filters for dates, you must use
   * [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
   *
   * ### See also
   *
   * To learn about working with Search methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language) and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
   * @param - Search criteria including filter, sort, aggregations, and paging options.
   */
  (search: ServiceSearch): Promise<
    NonNullablePaths<
      SearchServicesResponse,
      | `services`
      | `services.${number}.type`
      | `services.${number}.category._id`
      | `services.${number}.form._id`
      | `services.${number}.payment.rateType`
      | `services.${number}.payment.addOnOption`
      | `services.${number}.bookingPolicy._id`
      | `services.${number}.bookingPolicy.customPolicyDescription.enabled`
      | `services.${number}.bookingPolicy.customPolicyDescription.description`
      | `services.${number}.bookingPolicy.limitEarlyBookingPolicy.enabled`
      | `services.${number}.bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
      | `services.${number}.bookingPolicy.limitLateBookingPolicy.enabled`
      | `services.${number}.bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
      | `services.${number}.bookingPolicy.bookAfterStartPolicy.enabled`
      | `services.${number}.bookingPolicy.cancellationPolicy.enabled`
      | `services.${number}.bookingPolicy.cancellationPolicy.limitLatestCancellation`
      | `services.${number}.bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
      | `services.${number}.bookingPolicy.reschedulePolicy.enabled`
      | `services.${number}.bookingPolicy.reschedulePolicy.limitLatestReschedule`
      | `services.${number}.bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
      | `services.${number}.bookingPolicy.waitlistPolicy.enabled`
      | `services.${number}.bookingPolicy.waitlistPolicy.capacity`
      | `services.${number}.bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
      | `services.${number}.bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
      | `services.${number}.bookingPolicy.resourcesPolicy.enabled`
      | `services.${number}.bookingPolicy.resourcesPolicy.autoAssignAllowed`
      | `services.${number}.bookingPolicy.cancellationFeePolicy.enabled`
      | `services.${number}.bookingPolicy.saveCreditCardPolicy.enabled`
      | `services.${number}.schedule.availabilityConstraints.timeBetweenSessions`
      | `services.${number}.seoData.settings.preventAutoRedirect`
      | `services.${number}.taxableAddress.taxableAddressType`
      | `aggregationData.results`
      | `aggregationData.results.${number}.scalar.type`
      | `aggregationData.results.${number}.scalar.value`
      | `aggregationData.results.${number}.scalarDate.type`
      | `aggregationData.results.${number}.scalarDate.value`
      | `aggregationData.results.${number}.name`
      | `aggregationData.results.${number}.type`
      | `aggregationData.results.${number}.fieldPath`,
      6
    >
  >;
}

export function queryPolicies(httpClient: HttpClient): QueryPoliciesSignature {
  return (query: CursorQuery) =>
    universalQueryPolicies(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryPoliciesSignature {
  /**
   * Retrieves a list of up to 100 [booking policies](https://dev.wix.com/docs/api-reference/business-solutions/bookings/policies/booking-policies/introduction),
   * and information about the services that are connected to them,
   * given the provided filtering, paging, and sorting.
   *
   * ### Defaults
   *
   * Query Policies has the following default settings, which you can override:
   *
   * + Sorted by `id` in ascending order.
   * + `cursorPaging.limit` set to `100`.
   *
   * ### Filters
   *
   * When using date filters, you must use
   * [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
   *
   * ### Returned services
   *
   * If a booking policy is connected to more than 5 services, only a subset of
   * those services is returned. The `bookingPolicies.totalServiceCount` values
   * indicate the total number of services linked to each policy. You can call [Search Services](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/search-services)
   * and specify the relevant policy ID in the filter to retrieve all services
   * connected to the booking policy.
   *
   * ### See also
   *
   * To learn about working with Query methods, see
   * [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language)
   * and [Sorting and Paging](https://dev.wix.com/docs/rest/articles/getting-started/sorting-and-paging).
   * @param - Information about filters, paging, and sorting.
   */
  (query: CursorQuery): Promise<
    NonNullablePaths<
      QueryPoliciesResponse,
      | `bookingPolicies`
      | `bookingPolicies.${number}.bookingPolicy._id`
      | `bookingPolicies.${number}.bookingPolicy.customPolicyDescription.enabled`
      | `bookingPolicies.${number}.bookingPolicy.customPolicyDescription.description`
      | `bookingPolicies.${number}.bookingPolicy.limitEarlyBookingPolicy.enabled`
      | `bookingPolicies.${number}.bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
      | `bookingPolicies.${number}.bookingPolicy.limitLateBookingPolicy.enabled`
      | `bookingPolicies.${number}.bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
      | `bookingPolicies.${number}.bookingPolicy.bookAfterStartPolicy.enabled`
      | `bookingPolicies.${number}.bookingPolicy.cancellationPolicy.enabled`
      | `bookingPolicies.${number}.bookingPolicy.cancellationPolicy.limitLatestCancellation`
      | `bookingPolicies.${number}.bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
      | `bookingPolicies.${number}.bookingPolicy.reschedulePolicy.enabled`
      | `bookingPolicies.${number}.bookingPolicy.reschedulePolicy.limitLatestReschedule`
      | `bookingPolicies.${number}.bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
      | `bookingPolicies.${number}.bookingPolicy.waitlistPolicy.enabled`
      | `bookingPolicies.${number}.bookingPolicy.waitlistPolicy.capacity`
      | `bookingPolicies.${number}.bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
      | `bookingPolicies.${number}.bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
      | `bookingPolicies.${number}.bookingPolicy.resourcesPolicy.enabled`
      | `bookingPolicies.${number}.bookingPolicy.resourcesPolicy.autoAssignAllowed`
      | `bookingPolicies.${number}.bookingPolicy.cancellationFeePolicy.enabled`
      | `bookingPolicies.${number}.bookingPolicy.saveCreditCardPolicy.enabled`
      | `bookingPolicies.${number}.totalServiceCount`,
      6
    >
  >;
}

export function queryBookingForms(
  httpClient: HttpClient
): QueryBookingFormsSignature {
  return (query: CursorQuery, options?: QueryBookingFormsOptions) =>
    universalQueryBookingForms(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryBookingFormsSignature {
  /**
   * Retrieves a list of up to 100 booking [forms](https://dev.wix.com/docs/api-reference/crm/forms/form-schemas/form-object),
   * and information about the services that are connected to them,
   * given the provided filtering, paging, and sorting.
   *
   *
   * ### Defaults
   *
   * Query Booking Forms has the following default settings, which you can override:
   *
   * + Sorted by `id` in ascending order.
   * + `cursorPaging.limit` set to `100`.
   *
   * ### Filters
   *
   * When using date filters, you must use
   * [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
   *
   * ### Returned services
   *
   * If a booking policy is connected to more than 5 services, only a subset of
   * these service IDs and names is returned. The `bookingForms.totalServiceCount`
   * values indicate the total number of services linked to each form. You can call [Query Services](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/query-services)
   * and specify the relevant form ID in the filter to retrieve all services
   * connected to the booking form.
   *
   * ### Default booking forms
   *
   * By default, all Wix Bookings services use a standard booking form. To retrieve
   * a site's default booking form with Query Booking Forms, specify
   * `{"conditionalFields": ["DEFAULT_BOOKING_FORM"]}`.
   *
   * ### See also
   *
   * To learn about working with Query methods, see
   * [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language)
   * and [Sorting and Paging](https://dev.wix.com/docs/rest/articles/getting-started/sorting-and-paging).
   * @param - Information about filters, paging, and sorting. See the article about
   * form filters ([REST](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object))
   * for all supported filters and sorting options.
   */
  (query: CursorQuery, options?: QueryBookingFormsOptions): Promise<
    NonNullablePaths<
      QueryBookingFormsResponse,
      | `bookingForms`
      | `bookingForms.${number}.formDetails.formId`
      | `bookingForms.${number}.totalServiceCount`
      | `defaultBookingForm.formDetails.formId`
      | `defaultBookingForm.connectedServices`
      | `defaultBookingForm.totalServiceCount`,
      5
    > & {
      __applicationErrorsType?: QueryBookingFormsApplicationErrors;
    }
  >;
}

export function countServices(httpClient: HttpClient): CountServicesSignature {
  return (options?: CountServicesOptions) =>
    universalCountServices(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CountServicesSignature {
  /**
   * Counts how many services match the given filter.
   *
   *
   * See [Query Services](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/query-services) for a list of supported filters.
   *
   * To learn about working with filters in general, see
   * [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters)
   */
  (options?: CountServicesOptions): Promise<
    NonNullablePaths<CountServicesResponse, `count`, 2>
  >;
}

export function queryLocations(
  httpClient: HttpClient
): QueryLocationsSignature {
  return (options?: QueryLocationsOptions) =>
    universalQueryLocations(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryLocationsSignature {
  /**
   * Retrieves 3 separate lists of business, custom, and customer [locations](https://dev.wix.com/docs/api-reference/business-management/locations/introduction),
   * given the provided filtering, and whether each location is connected to at
   * least one of the site's services.
   *
   *
   * ### Defaults
   *
   * Query Locations has the following default setting, which you can't override:
   * Sorted by `id` in ascending order.
   *
   * ### Filters
   *
   * For a complete list of supported filters, refer to the `location` object
   * ([REST](https://dev.wix.com/docs/rest/business-management/locations/location-object)).
   *
   * When using date filters, you must use
   * [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
   *
   * ### See also
   *
   * To learn about working with Query methods, see
   * [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language)
   * and [Sorting and Paging](https://dev.wix.com/docs/rest/articles/getting-started/sorting-and-paging).
   */
  (options?: QueryLocationsOptions): Promise<
    NonNullablePaths<
      QueryLocationsResponse,
      | `businessLocations.exists`
      | `businessLocations.locations`
      | `businessLocations.locations.${number}.business._id`
      | `businessLocations.locations.${number}.business.name`
      | `businessLocations.locations.${number}.custom._id`
      | `businessLocations.locations.${number}._id`
      | `businessLocations.locations.${number}.type`
      | `customLocations.exists`
      | `customerLocations.exists`,
      6
    >
  >;
}

export function queryCategories(
  httpClient: HttpClient
): QueryCategoriesSignature {
  return (options?: QueryCategoriesOptions) =>
    universalQueryCategories(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryCategoriesSignature {
  /**
   * Retrieves a list of [service categories](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/categories-v2/introduction), given the provided filtering.
   *
   *
   * ### Defaults
   *
   * Query Categories has the following default setting, which you can't override:
   * Sorted by `id` in ascending order.
   *
   * ### Filters
   *
   * When using date filters, you must use
   * [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
   *
   * ### See also
   *
   * To learn about working with Query methods, see
   * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language)
   * and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
   */
  (options?: QueryCategoriesOptions): Promise<
    NonNullablePaths<
      QueryCategoriesResponse,
      `categories` | `categories.${number}._id`,
      4
    >
  >;
}

export function setServiceLocations(
  httpClient: HttpClient
): SetServiceLocationsSignature {
  return (serviceId: string, options?: SetServiceLocationsOptions) =>
    universalSetServiceLocations(
      serviceId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SetServiceLocationsSignature {
  /**
   * Replaces the list of the [locations](https://dev.wix.com/docs/api-reference/business-management/locations/introduction) where the business offers the service.
   *
   *
   * ### Consequences for customers
   *
   * Removing a service location may impact existing sessions and their
   * participants. If you remove at least one service location, you must
   * specify `removedLocationSessionsAction` to indicate what happens to all
   * future sessions scheduled at this location.
   *
   * - **Keep existing location**: If you want to retain future sessions at their
   * originally scheduled location, specify
   * `{"removedLocationSessionsAction.action": "KEEP_AT_CURRENT_LOCATION"}`.
   * This ensures nothing changes for the customer, but the business must be
   * able to provide access to the removed location in the future.
   * - **Update location**: If you want to update the location for future sessions
   * scheduled at the removed location, specify
   * `{"removedLocationSessionsAction.action": "MOVE_TO_LOCATION"}` and
   * `moveToLocationOptions.newLocation`.
   *
   * You can't mix and match to keep some sessions at the previous location while
   * moving other sessions to an updated location.
   *
   * Past session details aren't changed, no matter which option you choose for
   * future sessions.
   *
   * Future sessions scheduled for a location defined by the customer are also not
   * updated.
   *
   * ### Specify location details
   *
   * Depending on whether the new or updated location is a business or custom location,
   * you need to specify different fields.
   *
   * - **Business location**: Specify the relevant [location ID](https://dev.wix.com/docs/api-reference/business-management/locations/introduction) in `locations.business.id`.
   * - **Custom location**: Specify the complete address object as
   * `locations.custom.address`.
   *
   * ### Participant notifications
   *
   * You can specify a `participantNotification.message` that's immediately send
   * to all customers who had booked at a changed location. Ensure
   * `participantNotification.notifyParticipants` is set to `true` to send the
   * message.
   * @param - ID of the service.
   */
  (serviceId: string, options?: SetServiceLocationsOptions): Promise<
    NonNullablePaths<
      SetServiceLocationsResponse,
      | `service.type`
      | `service.media.items`
      | `service.category._id`
      | `service.form._id`
      | `service.payment.fixed.price.value`
      | `service.payment.fixed.price.currency`
      | `service.payment.rateType`
      | `service.payment.pricingPlanIds`
      | `service.payment.addOnOption`
      | `service.locations`
      | `service.locations.${number}.business._id`
      | `service.locations.${number}.business.name`
      | `service.locations.${number}.custom._id`
      | `service.locations.${number}._id`
      | `service.locations.${number}.type`
      | `service.bookingPolicy._id`
      | `service.bookingPolicy.customPolicyDescription.enabled`
      | `service.bookingPolicy.customPolicyDescription.description`
      | `service.bookingPolicy.limitEarlyBookingPolicy.enabled`
      | `service.bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
      | `service.bookingPolicy.limitLateBookingPolicy.enabled`
      | `service.bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
      | `service.bookingPolicy.bookAfterStartPolicy.enabled`
      | `service.bookingPolicy.cancellationPolicy.enabled`
      | `service.bookingPolicy.cancellationPolicy.limitLatestCancellation`
      | `service.bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
      | `service.bookingPolicy.reschedulePolicy.enabled`
      | `service.bookingPolicy.reschedulePolicy.limitLatestReschedule`
      | `service.bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
      | `service.bookingPolicy.waitlistPolicy.enabled`
      | `service.bookingPolicy.waitlistPolicy.capacity`
      | `service.bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
      | `service.bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
      | `service.bookingPolicy.resourcesPolicy.enabled`
      | `service.bookingPolicy.resourcesPolicy.autoAssignAllowed`
      | `service.bookingPolicy.cancellationFeePolicy.enabled`
      | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows`
      | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows.${number}.percentage`
      | `service.bookingPolicy.saveCreditCardPolicy.enabled`
      | `service.schedule.availabilityConstraints.durations`
      | `service.schedule.availabilityConstraints.durations.${number}.minutes`
      | `service.schedule.availabilityConstraints.sessionDurations`
      | `service.schedule.availabilityConstraints.timeBetweenSessions`
      | `service.staffMemberIds`
      | `service.serviceResources`
      | `service.supportedSlugs`
      | `service.supportedSlugs.${number}.name`
      | `service.seoData.tags`
      | `service.seoData.tags.${number}.type`
      | `service.seoData.tags.${number}.children`
      | `service.seoData.tags.${number}.custom`
      | `service.seoData.tags.${number}.disabled`
      | `service.seoData.settings.preventAutoRedirect`
      | `service.seoData.settings.keywords`
      | `service.seoData.settings.keywords.${number}.term`
      | `service.seoData.settings.keywords.${number}.isMain`
      | `service.addOnGroups`
      | `service.addOnDetails`
      | `service.taxableAddress.taxableAddressType`,
      7
    >
  >;
}

export function enablePricingPlansForService(
  httpClient: HttpClient
): EnablePricingPlansForServiceSignature {
  return (serviceId: string, pricingPlanIds: string[]) =>
    universalEnablePricingPlansForService(
      serviceId,
      pricingPlanIds,
      // @ts-ignore
      { httpClient }
    );
}

interface EnablePricingPlansForServiceSignature {
  /**
   * Adds a list of [pricing plan IDs](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/plans/plans-v3/introduction) to a service's `payment.pricingPlanIds` array.
   *
   *
   * The call doesn't validate whether the service's `payment.options.pricingPlan`
   * is set to `true`. If it's set to `false`, customers aren't able to pay
   * for their bookings using pricing plans. You can call [Update Service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/update-service) to change a service's supported payment methods.
   * @param - ID of the service to update.
   * @param - IDs of the [pricing plans](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/plans/introduction)
   * to add to the service's `payment.pricingPlanIds` array.
   * Maintained for backward compatibility. Use `pricing_plan_selections` for explicit benefit selection.
   */
  (serviceId: string, pricingPlanIds: string[]): Promise<
    NonNullablePaths<
      EnablePricingPlansForServiceResponse,
      | `service.type`
      | `service.media.items`
      | `service.category._id`
      | `service.form._id`
      | `service.payment.fixed.price.value`
      | `service.payment.fixed.price.currency`
      | `service.payment.rateType`
      | `service.payment.pricingPlanIds`
      | `service.payment.addOnOption`
      | `service.locations`
      | `service.locations.${number}.business._id`
      | `service.locations.${number}.business.name`
      | `service.locations.${number}.custom._id`
      | `service.locations.${number}._id`
      | `service.locations.${number}.type`
      | `service.bookingPolicy._id`
      | `service.bookingPolicy.customPolicyDescription.enabled`
      | `service.bookingPolicy.customPolicyDescription.description`
      | `service.bookingPolicy.limitEarlyBookingPolicy.enabled`
      | `service.bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
      | `service.bookingPolicy.limitLateBookingPolicy.enabled`
      | `service.bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
      | `service.bookingPolicy.bookAfterStartPolicy.enabled`
      | `service.bookingPolicy.cancellationPolicy.enabled`
      | `service.bookingPolicy.cancellationPolicy.limitLatestCancellation`
      | `service.bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
      | `service.bookingPolicy.reschedulePolicy.enabled`
      | `service.bookingPolicy.reschedulePolicy.limitLatestReschedule`
      | `service.bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
      | `service.bookingPolicy.waitlistPolicy.enabled`
      | `service.bookingPolicy.waitlistPolicy.capacity`
      | `service.bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
      | `service.bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
      | `service.bookingPolicy.resourcesPolicy.enabled`
      | `service.bookingPolicy.resourcesPolicy.autoAssignAllowed`
      | `service.bookingPolicy.cancellationFeePolicy.enabled`
      | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows`
      | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows.${number}.percentage`
      | `service.bookingPolicy.saveCreditCardPolicy.enabled`
      | `service.schedule.availabilityConstraints.durations`
      | `service.schedule.availabilityConstraints.durations.${number}.minutes`
      | `service.schedule.availabilityConstraints.sessionDurations`
      | `service.schedule.availabilityConstraints.timeBetweenSessions`
      | `service.staffMemberIds`
      | `service.serviceResources`
      | `service.supportedSlugs`
      | `service.supportedSlugs.${number}.name`
      | `service.seoData.tags`
      | `service.seoData.tags.${number}.type`
      | `service.seoData.tags.${number}.children`
      | `service.seoData.tags.${number}.custom`
      | `service.seoData.tags.${number}.disabled`
      | `service.seoData.settings.preventAutoRedirect`
      | `service.seoData.settings.keywords`
      | `service.seoData.settings.keywords.${number}.term`
      | `service.seoData.settings.keywords.${number}.isMain`
      | `service.addOnGroups`
      | `service.addOnDetails`
      | `service.taxableAddress.taxableAddressType`,
      7
    > & {
      __applicationErrorsType?: EnablePricingPlansForServiceApplicationErrors;
    }
  >;
}

export function disablePricingPlansForService(
  httpClient: HttpClient
): DisablePricingPlansForServiceSignature {
  return (serviceId: string, options?: DisablePricingPlansForServiceOptions) =>
    universalDisablePricingPlansForService(
      serviceId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DisablePricingPlansForServiceSignature {
  /**
   * Removes a list of [pricing plan IDs](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/plans/introduction) from a service's `payment.pricingPlanIds` array.
   *
   *
   * If you remove all pricing plan IDs from `payment.pricingPlanIds` and the
   * service supports only payments by pricing plan, customers will no longer be
   * able to book the service, as they will have no payment options available.
   * @param - ID of the service to update.
   */
  (serviceId: string, options?: DisablePricingPlansForServiceOptions): Promise<
    NonNullablePaths<
      DisablePricingPlansForServiceResponse,
      | `service.type`
      | `service.media.items`
      | `service.category._id`
      | `service.form._id`
      | `service.payment.fixed.price.value`
      | `service.payment.fixed.price.currency`
      | `service.payment.rateType`
      | `service.payment.pricingPlanIds`
      | `service.payment.addOnOption`
      | `service.locations`
      | `service.locations.${number}.business._id`
      | `service.locations.${number}.business.name`
      | `service.locations.${number}.custom._id`
      | `service.locations.${number}._id`
      | `service.locations.${number}.type`
      | `service.bookingPolicy._id`
      | `service.bookingPolicy.customPolicyDescription.enabled`
      | `service.bookingPolicy.customPolicyDescription.description`
      | `service.bookingPolicy.limitEarlyBookingPolicy.enabled`
      | `service.bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
      | `service.bookingPolicy.limitLateBookingPolicy.enabled`
      | `service.bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
      | `service.bookingPolicy.bookAfterStartPolicy.enabled`
      | `service.bookingPolicy.cancellationPolicy.enabled`
      | `service.bookingPolicy.cancellationPolicy.limitLatestCancellation`
      | `service.bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
      | `service.bookingPolicy.reschedulePolicy.enabled`
      | `service.bookingPolicy.reschedulePolicy.limitLatestReschedule`
      | `service.bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
      | `service.bookingPolicy.waitlistPolicy.enabled`
      | `service.bookingPolicy.waitlistPolicy.capacity`
      | `service.bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
      | `service.bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
      | `service.bookingPolicy.resourcesPolicy.enabled`
      | `service.bookingPolicy.resourcesPolicy.autoAssignAllowed`
      | `service.bookingPolicy.cancellationFeePolicy.enabled`
      | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows`
      | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows.${number}.percentage`
      | `service.bookingPolicy.saveCreditCardPolicy.enabled`
      | `service.schedule.availabilityConstraints.durations`
      | `service.schedule.availabilityConstraints.durations.${number}.minutes`
      | `service.schedule.availabilityConstraints.sessionDurations`
      | `service.schedule.availabilityConstraints.timeBetweenSessions`
      | `service.staffMemberIds`
      | `service.serviceResources`
      | `service.supportedSlugs`
      | `service.supportedSlugs.${number}.name`
      | `service.seoData.tags`
      | `service.seoData.tags.${number}.type`
      | `service.seoData.tags.${number}.children`
      | `service.seoData.tags.${number}.custom`
      | `service.seoData.tags.${number}.disabled`
      | `service.seoData.settings.preventAutoRedirect`
      | `service.seoData.settings.keywords`
      | `service.seoData.settings.keywords.${number}.term`
      | `service.seoData.settings.keywords.${number}.isMain`
      | `service.addOnGroups`
      | `service.addOnDetails`
      | `service.taxableAddress.taxableAddressType`,
      7
    > & {
      __applicationErrorsType?: DisablePricingPlansForServiceApplicationErrors;
    }
  >;
}

export function setCustomSlug(httpClient: HttpClient): SetCustomSlugSignature {
  return (serviceId: string, options?: SetCustomSlugOptions) =>
    universalSetCustomSlug(
      serviceId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SetCustomSlugSignature {
  /**
   * Sets a new active slug for the service.
   *
   *
   * The call fails if at least one of these conditions is met:
   * - The slug doesn't adheres to the supported format.
   * - Another service is currently using the slug.
   * - Another service has used the slug in the past.
   * @param - ID of the service for which to update the active slug.
   */
  (serviceId: string, options?: SetCustomSlugOptions): Promise<
    NonNullablePaths<
      SetCustomSlugResponse,
      | `slug.name`
      | `service.type`
      | `service.media.items`
      | `service.category._id`
      | `service.form._id`
      | `service.payment.fixed.price.value`
      | `service.payment.fixed.price.currency`
      | `service.payment.rateType`
      | `service.payment.pricingPlanIds`
      | `service.payment.addOnOption`
      | `service.locations`
      | `service.locations.${number}.business._id`
      | `service.locations.${number}.business.name`
      | `service.locations.${number}.custom._id`
      | `service.locations.${number}._id`
      | `service.locations.${number}.type`
      | `service.bookingPolicy._id`
      | `service.bookingPolicy.customPolicyDescription.enabled`
      | `service.bookingPolicy.customPolicyDescription.description`
      | `service.bookingPolicy.limitEarlyBookingPolicy.enabled`
      | `service.bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
      | `service.bookingPolicy.limitLateBookingPolicy.enabled`
      | `service.bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
      | `service.bookingPolicy.bookAfterStartPolicy.enabled`
      | `service.bookingPolicy.cancellationPolicy.enabled`
      | `service.bookingPolicy.cancellationPolicy.limitLatestCancellation`
      | `service.bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
      | `service.bookingPolicy.reschedulePolicy.enabled`
      | `service.bookingPolicy.reschedulePolicy.limitLatestReschedule`
      | `service.bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
      | `service.bookingPolicy.waitlistPolicy.enabled`
      | `service.bookingPolicy.waitlistPolicy.capacity`
      | `service.bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
      | `service.bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
      | `service.bookingPolicy.resourcesPolicy.enabled`
      | `service.bookingPolicy.resourcesPolicy.autoAssignAllowed`
      | `service.bookingPolicy.cancellationFeePolicy.enabled`
      | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows`
      | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows.${number}.percentage`
      | `service.bookingPolicy.saveCreditCardPolicy.enabled`
      | `service.schedule.availabilityConstraints.durations`
      | `service.schedule.availabilityConstraints.durations.${number}.minutes`
      | `service.schedule.availabilityConstraints.sessionDurations`
      | `service.schedule.availabilityConstraints.timeBetweenSessions`
      | `service.staffMemberIds`
      | `service.serviceResources`
      | `service.supportedSlugs`
      | `service.supportedSlugs.${number}.name`
      | `service.seoData.tags`
      | `service.seoData.tags.${number}.type`
      | `service.seoData.tags.${number}.children`
      | `service.seoData.tags.${number}.custom`
      | `service.seoData.tags.${number}.disabled`
      | `service.seoData.settings.preventAutoRedirect`
      | `service.seoData.settings.keywords`
      | `service.seoData.settings.keywords.${number}.term`
      | `service.seoData.settings.keywords.${number}.isMain`
      | `service.addOnGroups`
      | `service.addOnDetails`
      | `service.taxableAddress.taxableAddressType`,
      7
    > & {
      __applicationErrorsType?: SetCustomSlugApplicationErrors;
      __validationErrorsType?: SetCustomSlugValidationErrors;
    }
  >;
}

export function validateSlug(httpClient: HttpClient): ValidateSlugSignature {
  return (serviceId: string, options?: ValidateSlugOptions) =>
    universalValidateSlug(
      serviceId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ValidateSlugSignature {
  /**
   * Checks whether a custom slug is validate for the service.
   *
   *
   * The checks include:
   * - The slug adheres to the supported format.
   * - No other service is currently using the slug.
   * - No other service has used the slug in the past.
   *
   * The call fails if at least one of the checks fails.
   * @param - IO of the service to check custom slug validity for.
   */
  (serviceId: string, options?: ValidateSlugOptions): Promise<
    NonNullablePaths<ValidateSlugResponse, `valid` | `errors`, 2>
  >;
}

export function cloneService(httpClient: HttpClient): CloneServiceSignature {
  return (sourceServiceId: string) =>
    universalCloneService(
      sourceServiceId,
      // @ts-ignore
      { httpClient }
    );
}

interface CloneServiceSignature {
  /**
   * Clones a service.
   *
   *
   * ### Connected entities
   *
   * By default, not all entities connected to the service are cloned.
   *
   * #### Schedule
   *
   * Wix Bookings automatically creates a new active [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)
   * for the cloned service. If Wix Bookings can't create this schedule, the
   * Clone Service call fails.
   *
   * - __For appointment-based services__: Future appointments aren't added to the
   * cloned service's schedule. Use the [Events API](https://dev.wix.com/docs/api-reference/business-management/calendar/events-v3/introduction) to add them as needed.
   * - __For classes and courses__: Future one-time events aren't added to the
   * cloned service's schedule, while future recurring events are added
   * asynchronously. The Clone Service call always succeeds, whether or not
   * recurring events are added.
   * If the response includes `RECURRING_EVENTS` in the `errors` array, it means the
   * cloned service doesn't have future recurring events, even though the original
   * service does. You can either delete the cloned service and try again or use
   * the [Events API](https://dev.wix.com/docs/api-reference/business-management/calendar/events-v3/introduction) to add missing events to the schedule.
   *
   * Wix Bookings never adds past events to the cloned service's schedule.
   *
   * #### Service options and variants
   *
   * If the original service has [variants](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/service-options-and-variants/introduction),
   * they're cloned asynchronously. The Clone Service call always succeeds,
   * regardless of whether variants were cloned.
   *
   * If the response includes `OPTIONS_AND_VARIANTS` in the `errors` array, the cloned
   * service doesn't have variants, even though the original service does. You can
   * delete the cloned service and call Clone Service again, or use the
   * [Service Options And Variants API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/service-options-and-variants/introduction) to add variants.
   *
   * #### Booking form
   *
   * The original service's booking form isn't cloned, whether it's
   * the default or a custom booking form.
   *
   * #### Pricing plans
   *
   * If the original service's accepts payments via [pricing plans](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/introduction/),
   * the cloned service's `payment.options.pricingPlan` is also set to `true`. To
   * accept specific pricing plans, call [Enable Pricing Plans For Service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/enable-pricing-plans-for-service),
   * after cloning the service. If the original service accepts only
   * pricing plan payments and you don't call Enable Pricing Plans For Service
   * after cloning the service, customers will be unable to book the service.
   * @param - ID of the service to clone.
   */
  (sourceServiceId: string): Promise<
    NonNullablePaths<
      CloneServiceResponse,
      | `service.type`
      | `service.media.items`
      | `service.category._id`
      | `service.form._id`
      | `service.payment.fixed.price.value`
      | `service.payment.fixed.price.currency`
      | `service.payment.rateType`
      | `service.payment.pricingPlanIds`
      | `service.payment.addOnOption`
      | `service.locations`
      | `service.locations.${number}.business._id`
      | `service.locations.${number}.business.name`
      | `service.locations.${number}.custom._id`
      | `service.locations.${number}._id`
      | `service.locations.${number}.type`
      | `service.bookingPolicy._id`
      | `service.bookingPolicy.customPolicyDescription.enabled`
      | `service.bookingPolicy.customPolicyDescription.description`
      | `service.bookingPolicy.limitEarlyBookingPolicy.enabled`
      | `service.bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
      | `service.bookingPolicy.limitLateBookingPolicy.enabled`
      | `service.bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
      | `service.bookingPolicy.bookAfterStartPolicy.enabled`
      | `service.bookingPolicy.cancellationPolicy.enabled`
      | `service.bookingPolicy.cancellationPolicy.limitLatestCancellation`
      | `service.bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
      | `service.bookingPolicy.reschedulePolicy.enabled`
      | `service.bookingPolicy.reschedulePolicy.limitLatestReschedule`
      | `service.bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
      | `service.bookingPolicy.waitlistPolicy.enabled`
      | `service.bookingPolicy.waitlistPolicy.capacity`
      | `service.bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
      | `service.bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
      | `service.bookingPolicy.resourcesPolicy.enabled`
      | `service.bookingPolicy.resourcesPolicy.autoAssignAllowed`
      | `service.bookingPolicy.cancellationFeePolicy.enabled`
      | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows`
      | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows.${number}.percentage`
      | `service.bookingPolicy.saveCreditCardPolicy.enabled`
      | `service.schedule.availabilityConstraints.durations`
      | `service.schedule.availabilityConstraints.durations.${number}.minutes`
      | `service.schedule.availabilityConstraints.sessionDurations`
      | `service.schedule.availabilityConstraints.timeBetweenSessions`
      | `service.staffMemberIds`
      | `service.serviceResources`
      | `service.supportedSlugs`
      | `service.supportedSlugs.${number}.name`
      | `service.seoData.tags`
      | `service.seoData.tags.${number}.type`
      | `service.seoData.tags.${number}.children`
      | `service.seoData.tags.${number}.custom`
      | `service.seoData.tags.${number}.disabled`
      | `service.seoData.settings.preventAutoRedirect`
      | `service.seoData.settings.keywords`
      | `service.seoData.settings.keywords.${number}.term`
      | `service.seoData.settings.keywords.${number}.isMain`
      | `service.addOnGroups`
      | `service.addOnDetails`
      | `service.taxableAddress.taxableAddressType`
      | `errors`,
      7
    >
  >;
}

export function createAddOnGroup(
  httpClient: HttpClient
): CreateAddOnGroupSignature {
  return (
    addOnGroup: NonNullablePaths<AddOnGroup, `name`, 2>,
    options: NonNullablePaths<CreateAddOnGroupOptions, `serviceId`, 2>
  ) =>
    universalCreateAddOnGroup(
      addOnGroup,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateAddOnGroupSignature {
  /**
   * Creates a new add-on group for a service and updates the service's `addOnGroups` array.
   * @param - Add-on group to create.
   */
  (
    addOnGroup: NonNullablePaths<AddOnGroup, `name`, 2>,
    options: NonNullablePaths<CreateAddOnGroupOptions, `serviceId`, 2>
  ): Promise<
    NonNullablePaths<CreateAddOnGroupResponse, `addOnGroup.addOnIds`, 3>
  >;
}

export function deleteAddOnGroup(
  httpClient: HttpClient
): DeleteAddOnGroupSignature {
  return (
    addOnGroupId: string,
    options: NonNullablePaths<DeleteAddOnGroupOptions, `serviceId`, 2>
  ) =>
    universalDeleteAddOnGroup(
      addOnGroupId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteAddOnGroupSignature {
  /**
   * Deletes an add-on group.
   *
   *
   * Deleting the group doesn't delete individual add-ons.
   * Any add-on not associated with another group isn't available for customers to book.
   * @param - ID of the add-on group to delete.
   */
  (
    addOnGroupId: string,
    options: NonNullablePaths<DeleteAddOnGroupOptions, `serviceId`, 2>
  ): Promise<
    void & {
      __applicationErrorsType?: DeleteAddOnGroupApplicationErrors;
    }
  >;
}

export function updateAddOnGroup(
  httpClient: HttpClient
): UpdateAddOnGroupSignature {
  return (
    addOnGroup: NonNullablePaths<AddOnGroup, `_id`, 2>,
    options: NonNullablePaths<UpdateAddOnGroupOptions, `serviceId`, 2>
  ) =>
    universalUpdateAddOnGroup(
      addOnGroup,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateAddOnGroupSignature {
  /**
   * Updates an add-on group.
   *
   *
   * To update only the display order for add-ons, call [Set Add Ons For Group](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/set-add-ons-for-group) instead.
   *
   * You can't change which service an add-on group belongs to.
   * Each add-on group is permanently associated with the service it was created for.
   * To use the same add-ons with a different service, create a new add-on group for that service.
   * @param - Add-on group to update.
   */
  (
    addOnGroup: NonNullablePaths<AddOnGroup, `_id`, 2>,
    options: NonNullablePaths<UpdateAddOnGroupOptions, `serviceId`, 2>
  ): Promise<
    NonNullablePaths<UpdateAddOnGroupResponse, `addOnGroup.addOnIds`, 3> & {
      __applicationErrorsType?: UpdateAddOnGroupApplicationErrors;
    }
  >;
}

export function listAddOnGroupsByServiceId(
  httpClient: HttpClient
): ListAddOnGroupsByServiceIdSignature {
  return (serviceId: string, options?: ListAddOnGroupsByServiceIdOptions) =>
    universalListAddOnGroupsByServiceId(
      serviceId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListAddOnGroupsByServiceIdSignature {
  /**
   * Retrieves add-on groups for a service with enriched add-on details.
   *
   *
   * Add-on details are returned based on their display order.
   *
   * If you specify `groupIds`, only those groups are returned.
   * By default, all groups associated with the service are returned.
   * @param - ID of the service to retrieve add-on groups for.
   */
  (serviceId: string, options?: ListAddOnGroupsByServiceIdOptions): Promise<
    NonNullablePaths<
      ListAddOnGroupsByServiceIdResponse,
      `addOnGroupsDetails`,
      2
    >
  >;
}

export function setAddOnsForGroup(
  httpClient: HttpClient
): SetAddOnsForGroupSignature {
  return (
    serviceId: string,
    options: NonNullablePaths<
      SetAddOnsForGroupOptions,
      `addOnIds` | `groupId`,
      2
    >
  ) =>
    universalSetAddOnsForGroup(
      serviceId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SetAddOnsForGroupSignature {
  /**
   * Sets the add-ons for a specific group, replacing any existing add-ons in the group.
   *
   *
   * Call this method when you want to:
   * - Add new add-ons to a group.
   * - Remove add-ons from a group.
   * - Reorder existing add-ons for optimal customer presentation.
   *
   * The order of add-ons in the request determines their display sequence to customers.
   * All specified add-ons must already exist before adding them to a group.
   *
   * For updating other add-on group properties, for example `name`, `prompt`, or `constraints`, call [Update Add On Group](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/update-add-on-group) instead.
   * @param - Service ID containing the group.
   */
  (
    serviceId: string,
    options: NonNullablePaths<
      SetAddOnsForGroupOptions,
      `addOnIds` | `groupId`,
      2
    >
  ): Promise<
    NonNullablePaths<SetAddOnsForGroupResponse, `addOnGroup.addOnIds`, 3> & {
      __applicationErrorsType?: SetAddOnsForGroupApplicationErrors;
    }
  >;
}

export const onServiceCreated = EventDefinition(
  'wix.bookings.services.v2.service_created',
  true,
  (event: ServiceCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.payment.subscription.recurringStartDate' },
            { path: 'entity.bookingPolicy.createdDate' },
            { path: 'entity.bookingPolicy.updatedDate' },
            { path: 'entity.schedule.firstSessionStart' },
            { path: 'entity.schedule.lastSessionEnd' },
            { path: 'entity.supportedSlugs.createdDate' },
            { path: 'entity.mainSlug.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.media.items.image' },
            { path: 'entity.media.mainMedia.image' },
            { path: 'entity.media.coverMedia.image' },
            { path: 'entity.staffMembers.mainMedia.image' },
            { path: 'entity.staffMemberDetails.staffMembers.mainMedia.image' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.locations.calculatedAddress' },
            { path: 'entity.locations.business.address' },
            { path: 'entity.locations.custom.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'entity.urls.servicePage' },
            { path: 'entity.urls.bookingPage' },
            { path: 'entity.urls.calendarPage' },
          ],
        },
      ])
    )
)<ServiceCreatedEnvelope>();
export const onServiceDeleted = EventDefinition(
  'wix.bookings.services.v2.service_deleted',
  true,
  (event: ServiceDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.payment.subscription.recurringStartDate' },
            { path: 'entity.bookingPolicy.createdDate' },
            { path: 'entity.bookingPolicy.updatedDate' },
            { path: 'entity.schedule.firstSessionStart' },
            { path: 'entity.schedule.lastSessionEnd' },
            { path: 'entity.supportedSlugs.createdDate' },
            { path: 'entity.mainSlug.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.media.items.image' },
            { path: 'entity.media.mainMedia.image' },
            { path: 'entity.media.coverMedia.image' },
            { path: 'entity.staffMembers.mainMedia.image' },
            { path: 'entity.staffMemberDetails.staffMembers.mainMedia.image' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.locations.calculatedAddress' },
            { path: 'entity.locations.business.address' },
            { path: 'entity.locations.custom.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'entity.urls.servicePage' },
            { path: 'entity.urls.bookingPage' },
            { path: 'entity.urls.calendarPage' },
          ],
        },
      ])
    )
)<ServiceDeletedEnvelope>();
export const onServiceUpdated = EventDefinition(
  'wix.bookings.services.v2.service_updated',
  true,
  (event: ServiceUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.payment.subscription.recurringStartDate' },
            { path: 'entity.bookingPolicy.createdDate' },
            { path: 'entity.bookingPolicy.updatedDate' },
            { path: 'entity.schedule.firstSessionStart' },
            { path: 'entity.schedule.lastSessionEnd' },
            { path: 'entity.supportedSlugs.createdDate' },
            { path: 'entity.mainSlug.createdDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.payment.subscription.recurringStartDate' },
            { path: 'modifiedFields.bookingPolicy.createdDate' },
            { path: 'modifiedFields.bookingPolicy.updatedDate' },
            { path: 'modifiedFields.schedule.firstSessionStart' },
            { path: 'modifiedFields.schedule.lastSessionEnd' },
            { path: 'modifiedFields.supportedSlugs.createdDate' },
            { path: 'modifiedFields.mainSlug.createdDate' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.media.items.image' },
            { path: 'entity.media.mainMedia.image' },
            { path: 'entity.media.coverMedia.image' },
            { path: 'entity.staffMembers.mainMedia.image' },
            { path: 'entity.staffMemberDetails.staffMembers.mainMedia.image' },
            { path: 'modifiedFields.media.items.image' },
            { path: 'modifiedFields.media.mainMedia.image' },
            { path: 'modifiedFields.media.coverMedia.image' },
            { path: 'modifiedFields.staffMembers.mainMedia.image' },
            {
              path: 'modifiedFields.staffMemberDetails.staffMembers.mainMedia.image',
            },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.locations.calculatedAddress' },
            { path: 'entity.locations.business.address' },
            { path: 'entity.locations.custom.address' },
            { path: 'modifiedFields.locations.calculatedAddress' },
            { path: 'modifiedFields.locations.business.address' },
            { path: 'modifiedFields.locations.custom.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'entity.urls.servicePage' },
            { path: 'entity.urls.bookingPage' },
            { path: 'entity.urls.calendarPage' },
            { path: 'modifiedFields.urls.servicePage' },
            { path: 'modifiedFields.urls.bookingPage' },
            { path: 'modifiedFields.urls.calendarPage' },
          ],
        },
      ])
    )
)<ServiceUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  Action,
  ActionEvent,
  Actor,
  AddOn,
  AddOnAddOnInfoOneOf,
  AddOnDetails,
  AddOnGroup,
  AddOnGroupDetail,
  AddOnPaymentOptions,
  Address,
  AddressHint,
  AddressLocation,
  AddressStreetOneOf,
  Aggregation,
  AggregationData,
  AggregationKindOneOf,
  AggregationResults,
  AggregationResultsResultOneOf,
  AggregationResultsScalarDateResult,
  AggregationResultsScalarResult,
  AggregationType,
  ApplicationError,
  AvailabilityConstraints,
  BaseEventMetadata,
  Behavior,
  BehaviorBehaviorOneOf,
  Benefit,
  BenefitNotification,
  BenefitType,
  BookAfterStartPolicy,
  BookedAddOn,
  BookedEntity,
  BookedEntityItemOneOf,
  BookedResource,
  BookedSchedule,
  BookedSlot,
  Booking,
  BookingForm,
  BookingParticipantsInfoOneOf,
  BookingPolicy,
  BookingPolicyWithServices,
  BookingSource,
  BookingStatus,
  BulkActionMetadata,
  BulkCreateServicesOptions,
  BulkCreateServicesRequest,
  BulkCreateServicesResponse,
  BulkDeleteServicesByFilterOptions,
  BulkDeleteServicesByFilterRequest,
  BulkDeleteServicesByFilterResponse,
  BulkDeleteServicesOptions,
  BulkDeleteServicesRequest,
  BulkDeleteServicesResponse,
  BulkServiceResult,
  BulkUpdateServicesByFilterOptions,
  BulkUpdateServicesByFilterRequest,
  BulkUpdateServicesByFilterResponse,
  BulkUpdateServicesOptions,
  BulkUpdateServicesRequest,
  BulkUpdateServicesResponse,
  BusinessLocationOptions,
  BusinessLocations,
  BusinessSchedule,
  CancellationFeePolicy,
  CancellationPolicy,
  CancellationWindow,
  CancellationWindowFeeOneOf,
  Categories,
  Category,
  CategoryNotification,
  CategoryNotificationEvent,
  ChangeContext,
  ChangeContextPayloadOneOf,
  CloneErrors,
  CloneServiceRequest,
  CloneServiceResponse,
  CommonAddress,
  CommonAddressLocation,
  CommonAddressStreetOneOf,
  CommonIdentificationData,
  CommonIdentificationDataIdOneOf,
  CommonStreetAddress,
  CompletionRequirement,
  Conferencing,
  ConnectedService,
  ConsentPolicy,
  ContactDetails,
  CountServicesOptions,
  CountServicesRequest,
  CountServicesResponse,
  CreateAddOnGroupOptions,
  CreateAddOnGroupRequest,
  CreateAddOnGroupResponse,
  CreateServiceRequest,
  CreateServiceResponse,
  CrudType,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  CursorSearch,
  CursorSearchPagingMethodOneOf,
  Cursors,
  CustomFormField,
  CustomLocationOptions,
  CustomLocations,
  CustomOptions,
  CustomPayment,
  CustomerLocations,
  DateHistogramAggregation,
  DateHistogramResult,
  DateHistogramResults,
  DayOfWeek,
  DeleteAddOnGroupOptions,
  DeleteAddOnGroupRequest,
  DeleteAddOnGroupResponse,
  DeleteServiceOptions,
  DeleteServiceRequest,
  DeleteServiceResponse,
  DisablePricingPlansForServiceOptions,
  DisablePricingPlansForServiceRequest,
  DisablePricingPlansForServiceResponse,
  Discount,
  DiscountDiscountOneOf,
  DiscountInfo,
  DomainEvent,
  DomainEventBodyOneOf,
  DummyRequest,
  DummyResponse,
  Duration,
  Empty,
  EnablePricingPlansForServiceRequest,
  EnablePricingPlansForServiceResponse,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EntryPass,
  Event,
  EventMetadata,
  ExtendedFields,
  FieldViolation,
  FirstChargeDateType,
  FixedPayment,
  FlowControlSettings,
  Form,
  FormDetails,
  FormSettings,
  FrequencyType,
  FullUpfrontPayment,
  GeoCoordinates,
  GetServiceAvailabilityConstraintsRequest,
  GetServiceAvailabilityConstraintsResponse,
  GetServiceRequest,
  GetServiceResponse,
  GroupByAggregation,
  GroupByAggregationKindOneOf,
  GroupByValueResults,
  HtmlSitePublished,
  IdentificationData,
  IdentificationDataIdOneOf,
  IdentityType,
  IncludeMissingValuesOptions,
  IntakeFormPolicy,
  Interval,
  InvalidPricingPlan,
  InvalidSlugError,
  ItemMetadata,
  Keyword,
  LimitEarlyBookingPolicy,
  LimitLateBookingPolicy,
  ListAddOnGroupsByServiceIdOptions,
  ListAddOnGroupsByServiceIdRequest,
  ListAddOnGroupsByServiceIdResponse,
  Locale,
  Location,
  LocationType,
  LocationTypeEnumLocationType,
  MaskedService,
  Media,
  MediaItem,
  MediaItemItemOneOf,
  MessageEnvelope,
  MissingValues,
  Mode,
  Money,
  MoveToNewLocationsOptions,
  MultiServiceBookingInfo,
  MultiServiceBookingType,
  Multilingual,
  NestedAggregation,
  NestedAggregationItem,
  NestedAggregationItemKindOneOf,
  NestedAggregationResults,
  NestedAggregationResultsResultOneOf,
  NestedAggregationType,
  NestedResultValue,
  NestedResultValueResultOneOf,
  NestedResults,
  NestedValueAggregationResult,
  OnlineBooking,
  Page,
  Paging,
  PagingMetadataV2,
  ParticipantChoices,
  ParticipantNotification,
  ParticipantsPolicy,
  Payment,
  PaymentOptions,
  PaymentRateOneOf,
  PaymentStatus,
  PhoneCall,
  PlacementType,
  Platform,
  PolicyDescription,
  PricingPlanSelection,
  Properties,
  PropertiesChange,
  QueryBookingFormsOptions,
  QueryBookingFormsRequest,
  QueryBookingFormsResponse,
  QueryCategoriesFilter,
  QueryCategoriesOptions,
  QueryCategoriesRequest,
  QueryCategoriesResponse,
  QueryLocationsFilter,
  QueryLocationsOptions,
  QueryLocationsRequest,
  QueryLocationsResponse,
  QueryPoliciesRequest,
  QueryPoliciesResponse,
  QueryServicesMultiLanguageRequest,
  QueryServicesMultiLanguageResponse,
  QueryServicesOptions,
  QueryServicesRequest,
  QueryServicesResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RangeAggregation,
  RangeAggregationResult,
  RangeBucket,
  RangeResult,
  RangeResults,
  RankingOptions,
  RankingOrder,
  RateType,
  RemovedLocationSessionsAction,
  RemovedLocationSessionsActionActionOptionsOneOf,
  RequestedFields,
  ReschedulePolicy,
  ResolutionMethod,
  ResourceGroup,
  ResourceIds,
  ResourceSelection,
  ResourceTransparency,
  ResourceType,
  ResourcesPolicy,
  RestoreInfo,
  Results,
  SaveCreditCardPolicy,
  ScalarAggregation,
  ScalarDateResult,
  ScalarResult,
  ScalarType,
  Schedule,
  SearchDetails,
  SearchServicesRequest,
  SearchServicesResponse,
  SelectedPaymentOption,
  SelectionMethod,
  SeoSchema,
  Service,
  ServiceAvailabilityConstraints,
  ServiceChoice,
  ServiceChoiceChoiceOneOf,
  ServiceChoices,
  ServiceCreatedEnvelope,
  ServiceDeletedEnvelope,
  ServiceQuerySpec,
  ServiceResource,
  ServiceResourceSelectionOneOf,
  ServiceSearchSpec,
  ServiceType,
  ServiceUpdatedEnvelope,
  ServicesQueryBuilder,
  ServicesQueryResult,
  ServicesUrlsChanged,
  SetAddOnsForGroupOptions,
  SetAddOnsForGroupRequest,
  SetAddOnsForGroupResponse,
  SetCustomSlugEvent,
  SetCustomSlugOptions,
  SetCustomSlugRequest,
  SetCustomSlugResponse,
  SetServiceLocationsOptions,
  SetServiceLocationsRequest,
  SetServiceLocationsResponse,
  Settings,
  SiteCloned,
  SiteCreated,
  SitePropertiesEvent,
  SitePropertiesNotification,
  Slug,
  SortDirection,
  SortOrder,
  SortType,
  Sorting,
  SortingMethodType,
  SpecialHourPeriod,
  SplitInterval,
  StaffMediaItem,
  StaffMediaItemItemOneOf,
  StaffMember,
  StaffMemberDetails,
  StaffSortingPolicy,
  StaffSortingPolicyOptionsOneOf,
  Status,
  StreetAddress,
  Subdivision,
  SubscriptionPayment,
  SupportedLanguage,
  Tag,
  TaxableAddress,
  TaxableAddressType,
  TimePeriod,
  Timing,
  Translation,
  URLs,
  UpdateAddOnGroupOptions,
  UpdateAddOnGroupRequest,
  UpdateAddOnGroupResponse,
  UpdateService,
  UpdateServiceRequest,
  UpdateServiceResponse,
  UserDomainInfoChangedEvent,
  V2Category,
  V2Duration,
  V2Location,
  V2LocationOptionsOneOf,
  V2ParticipantNotification,
  V2PhoneCall,
  V2RequestedFields,
  V4Address,
  ValidateServiceRequest,
  ValidateServiceResponse,
  ValidateSlugOptions,
  ValidateSlugRequest,
  ValidateSlugResponse,
  ValueAggregation,
  ValueAggregationOptionsOneOf,
  ValueAggregationResult,
  ValueResult,
  ValueResults,
  ValueType,
  VariedPayment,
  WaitlistPolicy,
  WebhookIdentityType,
  utils,
} from './bookings-services-v2-service-services.universal.js';
